/**
 * 全站主题判据 —— **唯一规则，唯一实现**。
 *
 *   有 localStorage.umi_dark_mode → 用它（用户在系统里选过的色系）
 *   没有                         → 亮色（**不看系统偏好** prefers-color-scheme）
 *
 * 为什么要有这个文件（2026-10-19）：这条规则原本散在三处各写一遍 ——
 * 启动脚本 public/scripts/loading.js、app.tsx 的 getInitialDarkMode()、
 * TabLayout 的同步 effect。其中启动脚本那处曾在「没存储」时回退到系统偏好，
 * 于是暗色系统上开机会「启动动画先暗、React 起来后变亮」闪一下；
 * 登录页（独立页面，没有 TabLayout 做同步）也跟着错位。
 *
 * 现在 React 侧只有这一份读写实现，其余两侧调用它：
 *   · src/app.tsx                        initializeDarkMode() 读初值
 *   · src/components/TabLayout/index.tsx 同步 <html data-theme> + 切换时落盘
 *
 * ⚠️ 启动脚本是 bundle 之前的原生 JS，**无法 import 这里**，只能自己写一份等价实现；
 *    它的等价性由 src/themeRule.regression.test.cjs 盯着（那边不得出现系统偏好判据）。
 */

export const DARK_MODE_STORAGE_KEY = 'umi_dark_mode';

/** 读用户选择。没存过 / 读不到（隐身模式）一律亮色。 */
export function readStoredDarkMode(): boolean {
  try {
    return localStorage.getItem(DARK_MODE_STORAGE_KEY) === 'true';
  } catch (_e) {
    return false;
  }
}

/**
 * 把主题落到 <html data-theme>。
 *
 * 只做属性同步，不碰 localStorage —— 首屏/同步场景需要它：
 * React 提交之后才跑 effect 会把翻转拆成两拍（CSS 变量面等 effect、
 * antd token 面等 cssinjs），观感是「部分先变、部分延迟」。
 * 事件回调里同步调它 = 本帧绘制前生效，所有吃 CSS 变量的面同帧翻转。
 */
export function applyDarkModeAttribute(dark: boolean): void {
  if (typeof document === 'undefined') return;
  if (dark) {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    // 亮色必须**移除**属性：启动脚本可能已按存储值置过 dark，
    // 只写不管清会让暗色滞留整个会话，出现「CSS 变量面暗 / antd 面亮」的混合态。
    document.documentElement.removeAttribute('data-theme');
  }
}

/** 用户选定主题：落盘（下次启动沿用）+ 立即同步根属性。 */
export function persistDarkMode(dark: boolean): void {
  try {
    localStorage.setItem(DARK_MODE_STORAGE_KEY, String(dark));
  } catch (_e) {
    /* 隐身模式下可能写不进去，属性同步仍要生效 */
  }
  applyDarkModeAttribute(dark);
}

export default { DARK_MODE_STORAGE_KEY, readStoredDarkMode, applyDarkModeAttribute, persistDarkMode };